import { useCallback, useEffect, useState } from "react";

import { Building2, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

import DataTable from "../../components/shared/DataTable";
import PageHeader from "../../components/shared/PageHeader";
import Pagination from "../../components/shared/Pagination";
import SearchInput from "../../components/shared/SearchInput";
import WordCount from "../../components/shared/WordCount.jsx";
import { Badge } from "../../components/ui/badge.jsx";
import { Button } from "../../components/ui/button.jsx";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog.jsx";
import { Input } from "../../components/ui/input.jsx";
import { Label } from "../../components/ui/label.jsx";
import { Textarea } from "../../components/ui/textarea.jsx";
import { useApi } from "../../context/ApiContext";
import { useConfirm } from "../../context/ConfirmContext.jsx";
import { usePagination } from "../../hooks/usePagination";
import { cn, getCharCount } from "../../lib/utils.js";

const TenantManagement = () => {
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newTenant, setNewTenant] = useState({
    name: "",
    tenantKey: "",
    description: "",
    plan: "pro"
  });
  const [editingTenant, setEditingTenant] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const api = useApi();

  const pagination = usePagination(10);
  const { items: tenants, currentPage: page, pageSize, setPaginationData, setCurrentPage: setPage } = pagination;

  const isNewDescriptionOverLimit = getCharCount(newTenant.description) > 500;
  const isEditDescriptionOverLimit = editingTenant ? getCharCount(editingTenant.description) > 500 : false;

  const fetchTenants = useCallback(async () => {
    try {
      const response = await api.get("/tenants", {
        params: {
          page,
          size: pageSize,
          search: searchTerm.trim() || undefined
        }
      });
      setPaginationData(response.data);
    } catch (error) {
      toast.error("Failed to fetch tenants");
    } finally {
      setLoading(false);
    }
  }, [api, page, pageSize, searchTerm, setPaginationData]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (isNewDescriptionOverLimit) {
      toast.error("Description exceeds the 500-character limit.");
      return;
    }
    try {
      await api.post("/tenants", newTenant);
      toast.success("Tenant created successfully");
      setIsCreateOpen(false);
      setNewTenant({ name: "", tenantKey: "", description: "", plan: "pro" });
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create tenant");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (isEditDescriptionOverLimit) {
      toast.error("Description exceeds the 500-character limit.");
      return;
    }
    try {
      await api.put(`/tenants/${editingTenant.id}`, editingTenant);
      toast.success("Tenant updated successfully");
      setIsEditOpen(false);
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update tenant");
    }
  };

  const confirm = useConfirm();

  const handleDelete = async (id) => {
    const isConfirmed = await confirm({
      title: "Delete Tenant",
      message: "Are you sure you want to delete this tenant?"
    });
    if (isConfirmed) {
      try {
        await api.delete(`/tenants/${id}`);
        toast.success("Tenant deleted successfully");
        fetchTenants();
      } catch (error) {
        toast.error("Failed to delete tenant");
      }
    }
  };

  const columns = [
    {
      header: "Tenant Name",
      accessor: "name",
      isPrimary: true
    },
    {
      header: "Key",
      accessor: "tenantKey",
      render: (val) => <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">{val}</code>
    },
    {
      header: "Plan",
      accessor: "plan",
      render: (val) => (
        <Badge variant="outline" className="capitalize">
          {val}
        </Badge>
      )
    },
    {
      header: "Status",
      accessor: "status",
      render: () => (
        <Badge variant="success" className="bg-green-500/10 text-green-600 border-green-200">
          Active
        </Badge>
      )
    },
    {
      header: "Actions",
      render: (_, tenant) => (
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={(e) => {
              e.stopPropagation();
              setEditingTenant({ ...tenant });
              setIsEditOpen(true);
            }}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(tenant.id);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <PageHeader
        title="Tenant Management"
        subtitle="Manage platform tenants and their subscriptions"
        actionLabel="New Tenant"
        onActionClick={() => setIsCreateOpen(true)}
        pageIcon={Building2}
        extraActions={
          <SearchInput
            placeholder="Search tenants..."
            className="md:w-72"
            onSearch={(val) => {
              setSearchTerm(val);
              setPage(0);
            }}
          />
        }
      />

      <DataTable
        columns={columns}
        data={tenants}
        emptyStateTitle="No tenants found"
        emptyStateDescription="Add a new organization to get started."
        emptyStateIcon={Building2}
      />

      <div className="mt-8">
        <Pagination pagination={pagination} />
      </div>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] p-0 overflow-hidden flex flex-col">
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle>Create New Tenant</DialogTitle>
              <DialogDescription>Add a new organization to the platform.</DialogDescription>
            </DialogHeader>
          </div>
          <form onSubmit={handleCreate} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  Organization Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  placeholder="e.g. Acme Corp"
                  value={newTenant.name}
                  onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="key">
                  Tenant Key (Slug) <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="key"
                  placeholder="e.g. acme-corp"
                  value={newTenant.tenantKey}
                  onChange={(e) => setNewTenant({ ...newTenant, tenantKey: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newTenant.description}
                  onChange={(e) => setNewTenant({ ...newTenant, description: e.target.value })}
                  className={cn(isNewDescriptionOverLimit && "border-red-500")}
                  rows={3}
                  placeholder="Organization description"
                />
                <WordCount text={newTenant.description} />
              </div>
            </div>
            <div className="p-6 pt-2 border-t bg-white dark:bg-slate-950 sticky bottom-0 z-10 flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" className="flex-1" disabled={isNewDescriptionOverLimit}>
                Create Tenant
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] p-0 overflow-hidden flex flex-col">
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle>Edit Tenant</DialogTitle>
              <DialogDescription>Update organization details.</DialogDescription>
            </DialogHeader>
          </div>
          {editingTenant && (
            <form onSubmit={handleUpdate} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">
                    Organization Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="edit-name"
                    value={editingTenant.name}
                    onChange={(e) =>
                      setEditingTenant({
                        ...editingTenant,
                        name: e.target.value
                      })
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-key">
                    Tenant Key (Slug) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="edit-key"
                    value={editingTenant.tenantKey}
                    onChange={(e) =>
                      setEditingTenant({
                        ...editingTenant,
                        tenantKey: e.target.value
                      })
                    }
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    value={editingTenant.description || ""}
                    onChange={(e) =>
                      setEditingTenant({
                        ...editingTenant,
                        description: e.target.value
                      })
                    }
                    className={cn(isEditDescriptionOverLimit && "border-red-500")}
                    rows={3}
                  />
                  <WordCount text={editingTenant.description} />
                </div>
              </div>
              <div className="p-6 pt-2 border-t bg-white dark:bg-slate-950 sticky bottom-0 z-10 flex gap-3">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setIsEditOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" className="flex-1" disabled={isEditDescriptionOverLimit}>
                  Save Changes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantManagement;
